import { isString, isBoolean, isNumber } from './runtime-values.js';
import type { DataFeature, DataLayer, DataLayerDisplay, DataLayerStyle } from './data-layer.js';

/** Encoded primitive values retain the difference between strings, numbers and null. */
export function fieldValue(value: DataFeature['properties'][string]): string | null {
  return value === null || isString(value) || isBoolean(value) || (isNumber(value) && Number.isFinite(value)) ? JSON.stringify(value) : null;
}

export function matchesFieldFilter(feature: DataFeature, display?: DataLayerDisplay): boolean {
  if (display?.fieldFilter && fieldValue(feature.properties[display.fieldFilter.field]) !== display.fieldFilter.value) return false;
  return (display?.filters ?? []).every(filter => {
    const value = feature.properties[filter.field];
    if (filter.operator === 'eq') return fieldValue(value) === filter.value;
    if (filter.operator === 'neq') return fieldValue(value) !== filter.value;
    if (filter.operator === 'contains') return isString(value) && value.toLocaleLowerCase().includes(filter.value.toLocaleLowerCase());
    if (!isNumber(value) || !Number.isFinite(value) || !filter.value.trim() || !Number.isFinite(Number(filter.value))) return false;
    const limit = Number(filter.value);
    if (filter.operator === 'gt') return value > limit;
    if (filter.operator === 'gte') return value >= limit;
    if (filter.operator === 'lt') return value < limit;
    return value <= limit;
  });
}

export function dataFeatureStyle(feature: DataFeature, layer: Pick<DataLayer, 'style' | 'display'>): DataLayerStyle {
  const style = { ...layer.style, ...feature.sourceStyle };
  const theme = layer.display?.colorBy;
  const value = theme ? feature.properties[theme.field] : null;
  const category = theme?.numeric
    ? (isNumber(value) && Number.isFinite(value) ? theme.categories.find(item => item.upperBound === undefined || value < item.upperBound) : undefined)
    : theme?.categories.find(item => item.value === fieldValue(value));
  return category ? { ...style, color: category.color, fillColor: category.color } : style;
}
